import React from 'react';
import { BUCKETS } from './bucket-theme';
import { DoneToggleButton } from './done-toggle-button';
import type { WrkthruIssue } from 'shared/src/wrkthru/types';
import './wrkthru.css';

type IssueCardProps = {
  issue: WrkthruIssue;
  onWrkthruIt?: (issue: WrkthruIssue) => void;
  /**
   * Operator self-reports that they've resolved the issue — the only signal we
   * get, since the real work happens in another system (Bravo, the DSP, Zoho)
   * with no callback back to Wrkthru_.
   */
  onMarkDone?: () => void;
  onUndone?: () => void;
};

export function IssueCard({ issue, onWrkthruIt, onMarkDone, onUndone }: IssueCardProps) {
  const accent = BUCKETS[issue.bucket].accent;

  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <span
        className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: accent.hex }}
        aria-hidden="true"
      />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span
            className={[
              'inline-flex items-center rounded px-1.5 py-0.5 font-semibold uppercase tracking-wide',
              accent.bg,
              accent.text
            ].join(' ')}>
            {issue.statusLabel}
          </span>
          {issue.badge && (
            <span className="inline-flex items-center rounded bg-emerald-100 px-1.5 py-0.5 font-semibold uppercase tracking-wide text-emerald-700">
              {issue.badge}
            </span>
          )}
          <span className="text-slate-400">
            · {issue.scope === 'line-item' ? 'Line item' : 'Campaign'}
          </span>
        </div>
        <div className="mt-1">
          <a
            href={issue.entityHref}
            className="text-base font-semibold text-slate-900 hover:underline">
            {issue.entityName}
          </a>
          {issue.entitySecondary && (
            <p className="text-xs text-slate-500">{issue.entitySecondary}</p>
          )}
        </div>
        <div className="wrkthru-llm-card mt-2">
          <p className="text-sm leading-snug text-slate-700">{issue.description}</p>
          {issue.whyNow && (
            <p className="mt-1.5 text-xs leading-snug text-slate-600">
              <span className="font-semibold text-slate-700">Reasoning:</span> {issue.whyNow}
            </p>
          )}
        </div>
      </div>

      <div className="flex shrink-0 flex-col items-stretch gap-2 self-start">
        <DoneToggleButton onMarkDone={onMarkDone} onUndone={onUndone} />
        <button
          type="button"
          onClick={() => onWrkthruIt?.(issue)}
          className="wrkthru-gradient-btn px-3 py-1.5 text-xs">
          <span className="wrkthru-gradient-text font-semibold">Wrkthru_ it</span>
        </button>
      </div>
    </div>
  );
}
